import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Box,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import { Marker, Source, Layer, type MapRef } from '@vis.gl/react-maplibre';
import MapView from 'components/Map/MapView';
import { applyNeutralBackdrop, setBasemap } from 'utils/maplibre';
import { hasNoSatelliteCoverage } from 'utils/siteRoadOverlay';
import { useMapFlyTo } from 'hooks/useMapFlyTo';
import { zoneGeogToGeoPoints } from 'utils/geohash';
import { type GeoJSONPolygon } from 'api/location-importer.api';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import { useAppSelector } from 'store/hook';
import { useMineMapOverlay } from 'views/DispatchImporters/RoadsImporter/useMineMapOverlay';
import { CAR_CUSTOMER_CODE } from 'views/DispatchImporters/RoadsImporter/roadsImporter.constants';
import { getImageNameByMakeModelAndAsset } from 'utils/helpers/mapAssetImage';
import { images } from 'components/Asset/PNG';

export interface ZoneMapAsset {
  bumperNumber: string;
  assetType: string;
  make?: string;
  model?: string;
  lat: number;
  lon: number;
}

export interface ZoneMapLocation {
  name: string;
  locationType?: string | null;
  centroidLat: number | null;
  centroidLon: number | null;
  zoneGeog: GeoJSONPolygon | null;
  asset?: ZoneMapAsset | null;
}

// Same make/model-specific lookup Fleet Overview's AssetMap uses, so an
// asset looks the same wherever it's rendered on a map in this app
function getAssetPng(
  make: string | undefined,
  model: string | undefined,
  assetType: string
): string {
  const pngKey = getImageNameByMakeModelAndAsset(
    make ?? '',
    model ?? '',
    assetType,
    'Small'
  );
  return images[pngKey] ?? '';
}

interface LocationZoneMapModalProps {
  open: boolean;
  onClose: () => void;
  location: ZoneMapLocation | null;
}

// Dark, high-contrast pill used for labels drawn directly on top of map
// imagery (satellite/neutral-road tiles) — kept as fixed colors independent
// of the app theme, same as every other migrated map overlay (DispatchMap,
// AssetMap), since these need consistent contrast over photographic tiles
// rather than following light/dark app theme.
const labelPillSx = {
  px: 1,
  py: 0.25,
  borderRadius: '4px',
  bgcolor: 'rgba(10,15,30,0.75)',
  whiteSpace: 'nowrap' as const,
};

// Reusable single-location map modal: draws the location's zoneGeog polygon
// (falling back to a point marker when no zone is drawn), and — for the
// customer already gated to the Mine Map overlay elsewhere in the app — adds
// that same overlay on top of the base layer so this modal stays visually
// consistent with Mine Map Studio for that customer. Every other customer
// just sees the plain satellite/neutral-road base, same as before.
const LocationZoneMapModalContent = memo(function LocationZoneMapModalContent({
  open,
  onClose,
  location,
}: LocationZoneMapModalProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const mapRef = useRef<MapRef | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [hasCameraFlown, setHasCameraFlown] = useState(false);

  const {
    code: customerCode,
    proj4String,
    localOriginEasting,
    localOriginNorthing,
  } = useAppSelector((s) => s.persistedReducer.customer);

  useMineMapOverlay({
    mapRef,
    enabled: customerCode === CAR_CUSTOMER_CODE,
    mapReady,
    showRoadsOverlay: true,
    proj4String,
    localOriginEasting,
    localOriginNorthing,
  });

  const { flyTo } = useMapFlyTo(mapRef);

  const hull = useMemo(
    () => zoneGeogToGeoPoints(location?.zoneGeog),
    [location]
  );

  // GeoJSON polygon ring for the zone boundary — [lon, lat] pairs, closed
  // (first point repeated as the last) as GeoJSON requires.
  const hullRing = useMemo<number[][] | null>(() => {
    if (hull.length < 3) return null;
    const ring = hull.map((p) => [p.lon, p.lat]);
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) ring.push(first);
    return ring;
  }, [hull]);

  const centerCoords = useMemo(() => {
    if (hull.length >= 3) {
      const lat = hull.reduce((s, p) => s + p.lat, 0) / hull.length;
      const lon = hull.reduce((s, p) => s + p.lon, 0) / hull.length;
      return { lat, lon };
    }
    const lat = location?.centroidLat;
    const lon = location?.centroidLon;
    if (
      lat != null &&
      lon != null &&
      lat !== 0 &&
      lon !== 0 &&
      lat >= -90 &&
      lat <= 90 &&
      lon >= -180 &&
      lon <= 180
    ) {
      return { lat, lon };
    }
    return null;
  }, [hull, location]);

  const markerColor = theme.palette.primary.main;

  const applyImagery = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map?.isStyleLoaded()) return false;
    if (hasNoSatelliteCoverage(customerCode)) {
      applyNeutralBackdrop(map);
    } else {
      setBasemap(
        map,
        customerCode === CAR_CUSTOMER_CODE ? 'neutralRoad' : 'satellite'
      );
    }
    setMapReady(true);
    return true;
  }, [customerCode]);

  useEffect(() => {
    if (!open) {
      setMapReady(false);
      setHasCameraFlown(false);
      return;
    }
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;
    const tryApply = () => {
      if (cancelled) return;
      if (applyImagery()) return;
      if (attempts >= 20) return;
      attempts += 1;
      timer = setTimeout(tryApply, 200);
    };
    tryApply();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [open, applyImagery]);

  useEffect(() => {
    if (!mapReady || hasCameraFlown || !centerCoords) return;
    setHasCameraFlown(true);
    // Zoom picked to look similarly close to the old Cesium altitudes
    // (900m with a drawn zone, 1500m for a bare point).
    flyTo(centerCoords.lat, centerCoords.lon, hull.length >= 3 ? 17 : 16);
  }, [mapReady, hasCameraFlown, centerCoords, hull, flyTo]);

  const showMap = centerCoords !== null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: (th) => alpha(th.palette.divider, 0.5),
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          pt: 2,
          pb: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: (th) => alpha(th.palette.divider, 0.4),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '9px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: (th) => alpha(th.palette.primary.main, 0.12),
              border: '1px solid',
              borderColor: (th) => alpha(th.palette.primary.main, 0.3),
              flexShrink: 0,
            }}
          >
            <FmdGoodOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              {location?.name ?? t('location_zone_map_title')}
            </Typography>
            {location?.locationType && (
              <Typography variant="caption" color="text.secondary">
                {location.locationType}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, height: 420, position: 'relative' }}>
        {!showMap ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              bgcolor: 'background.default',
            }}
          >
            <FmdGoodOutlinedIcon
              sx={{ fontSize: 48, color: 'text.disabled' }}
            />
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              {t('location_zone_map_invalid')}
            </Typography>
          </Box>
        ) : (
          <>
            {!mapReady && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'background.default',
                  zIndex: 5,
                }}
              >
                <CircularProgress size={32} />
              </Box>
            )}

            <Box sx={{ width: '100%', height: '100%' }}>
              <MapView ref={mapRef}>
                {hullRing && (
                  <Source
                    id="location-zone-polygon"
                    type="geojson"
                    data={{
                      type: 'Feature',
                      properties: {},
                      geometry: { type: 'Polygon', coordinates: [hullRing] },
                    }}
                  >
                    <Layer
                      id="location-zone-fill"
                      type="fill"
                      paint={{
                        'fill-color': markerColor,
                        'fill-opacity': 0.25,
                      }}
                    />
                    <Layer
                      id="location-zone-outline"
                      type="line"
                      paint={{
                        'line-color': markerColor,
                        'line-width': 2,
                        'line-opacity': 0.85,
                      }}
                    />
                  </Source>
                )}

                {!hullRing && centerCoords && (
                  <Marker
                    longitude={centerCoords.lon}
                    latitude={centerCoords.lat}
                  >
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        backgroundColor: markerColor,
                        border: '2px solid #fff',
                      }}
                    />
                  </Marker>
                )}

                {centerCoords && location?.name && (
                  <Marker
                    longitude={centerCoords.lon}
                    latitude={centerCoords.lat}
                    anchor="bottom"
                    offset={[0, -12]}
                  >
                    <Box sx={labelPillSx}>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: '#fff',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {location.name}
                      </Typography>
                    </Box>
                  </Marker>
                )}

                {location?.asset && (
                  <Marker
                    longitude={location.asset.lon}
                    latitude={location.asset.lat}
                    anchor="top"
                    offset={[0, 6]}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <Box
                        component="img"
                        src={getAssetPng(
                          location.asset.make,
                          location.asset.model,
                          location.asset.assetType
                        )}
                        sx={{ height: 24 }}
                      />
                      <Box sx={labelPillSx}>
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: '#fff',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {location.asset.bumperNumber}
                        </Typography>
                      </Box>
                    </Box>
                  </Marker>
                )}
              </MapView>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 1.5,
          borderTop: '1px solid',
          borderColor: (th) => alpha(th.palette.divider, 0.4),
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          onClick={onClose}
          sx={{ borderRadius: '8px', fontWeight: 600 }}
        >
          {t('location_zone_map_close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export const LocationZoneMapModal = (props: LocationZoneMapModalProps) => (
  <CustomMuiThemeProvider>
    <LocationZoneMapModalContent {...props} />
  </CustomMuiThemeProvider>
);
